/**
 * Übersicht nach dem Scannen — „Seite 1 von n".
 *
 * Hier wird geprüft und nachgebessert, bevor irgendetwas hochgeladen wird:
 * Anpassen (Ecken), Filter, Drehen, Wiederholen, Löschen. Wischen blättert.
 * Erst „Weiter" führt zur Zuordnung; vorher verlässt nichts das Telefon.
 */
import { useEffect, useRef, useState } from "react";
import type { Filter } from "../../lib/scan/bild";
import { FILTER_NAMEN, MAX_SEITEN, type ScanSeite } from "../../lib/scan/seite";
import { ScanIcon } from "./ScanIcon";

type Props = {
  seiten: ScanSeite[];
  index: number;
  onIndex: (i: number) => void;
  onZurKamera: () => void;
  onAnpassen: (id: string) => void;
  onFilter: (id: string, f: Filter) => void;
  onDrehen: (id: string) => void;
  onWiederholen: (id: string) => void;
  onLoeschen: (id: string) => void;
  onWeiter: () => void;
};

export default function ScanUebersicht({
  seiten,
  index,
  onIndex,
  onZurKamera,
  onAnpassen,
  onFilter,
  onDrehen,
  onWiederholen,
  onLoeschen,
  onWeiter,
}: Props) {
  const bahnRef = useRef<HTMLDivElement>(null);
  const [filterOffen, setFilterOffen] = useState(false);
  const i = Math.max(0, Math.min(index, seiten.length - 1));
  const seite = seiten[i];

  // Von außen gesetzten Index anfahren (z. B. nach Löschen)
  useEffect(() => {
    const b = bahnRef.current;
    if (!b) return;
    const ziel = i * b.clientWidth;
    if (Math.abs(b.scrollLeft - ziel) > 4) b.scrollTo({ left: ziel, behavior: "auto" });
  }, [i, seiten.length]);

  const beimBlaettern = () => {
    const b = bahnRef.current;
    if (!b || !b.clientWidth) return;
    const n = Math.round(b.scrollLeft / b.clientWidth);
    if (n !== i && n >= 0 && n < seiten.length) {
      onIndex(n);
      setFilterOffen(false);
    }
  };

  if (!seite) return null;
  const rechnet = seiten.filter((s) => !s.ergebnis && !s.fehler).length;

  return (
    <div className="sc-uebersicht">
      <div className="sc-ue-kopf">
        <button className="sc-textknopf" onClick={onZurKamera}>
          <ScanIcon name="zurueck" /> Kamera
        </button>
        <div className="sc-ue-titel">
          Seite {i + 1} von {seiten.length}
        </div>
        <span className="sc-ue-platz" />
      </div>

      <div className="sc-bahn" ref={bahnRef} onScroll={beimBlaettern}>
        {seiten.map((s) => (
          <div className="sc-blatt" key={s.id}>
            <img
              src={s.ergebnis?.url ?? s.vorschau}
              alt=""
              className={s.ergebnis ? "" : "is-vorlaeufig"}
              draggable={false}
            />
            {!s.ergebnis && !s.fehler && <div className="sc-rechnet" aria-label="wird aufbereitet" />}
          </div>
        ))}
      </div>

      {(seite.unscharf || !seite.erkannt || seite.fehler) && (
        <div className="sc-ue-hinweis">
          <ScanIcon name="warnung" />
          <span>
            {seite.fehler
              ? "Diese Seite ließ sich nicht aufbereiten — bitte neu aufnehmen."
              : seite.unscharf
                ? "Diese Seite ist unscharf. Besser neu aufnehmen."
                : "Rand nicht sicher erkannt — mit „Anpassen“ prüfen."}
          </span>
        </div>
      )}

      {filterOffen && (
        <div className="sc-filterwahl">
          {(Object.keys(FILTER_NAMEN) as Filter[]).map((f) => (
            <button
              key={f}
              className={"sc-filter" + (seite.filter === f ? " is-gewaehlt" : "")}
              onClick={() => {
                onFilter(seite.id, f);
                setFilterOffen(false);
              }}
            >
              <b>{FILTER_NAMEN[f].name}</b>
              <span>{FILTER_NAMEN[f].info}</span>
            </button>
          ))}
        </div>
      )}

      <div className="sc-werkzeuge">
        <button onClick={() => onAnpassen(seite.id)}>
          <ScanIcon name="anpassen" />
          <span>Anpassen</span>
        </button>
        <button className={filterOffen ? "is-an" : ""} onClick={() => setFilterOffen((o) => !o)}>
          <ScanIcon name="filter" />
          <span>Filter</span>
        </button>
        <button onClick={() => onDrehen(seite.id)}>
          <ScanIcon name="drehen" />
          <span>Drehen</span>
        </button>
        <button onClick={() => onWiederholen(seite.id)}>
          <ScanIcon name="wiederholen" />
          <span>Wiederholen</span>
        </button>
        <button onClick={() => onLoeschen(seite.id)}>
          <ScanIcon name="loeschen" />
          <span>Löschen</span>
        </button>
      </div>

      <div className="sc-ue-fuss">
        <button className="sc-knopf" onClick={onZurKamera} disabled={seiten.length >= MAX_SEITEN}>
          <ScanIcon name="plus" /> Seite
        </button>
        <button className="sc-knopf sc-knopf-primaer" onClick={onWeiter}>
          {rechnet > 0 ? "Weiter …" : "Weiter"}
        </button>
      </div>
    </div>
  );
}
